import {
  memo,
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
} from 'react';
import { Stack, Text } from '@mantine/core';
import { IconNote } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { ListItemSkeleton } from '@/components/common';
import { NoteCard } from '@/components/notes/NoteCard';
import { useArtifactNavigation } from '@/hooks/useArtifactNavigation';
import { useGeoMarkupStore } from '@/store';
import type { Note } from '@/types/notes';

import styles from './TaskLinkedNotes.module.css';

/** Linked note outside the current dataset — title visible, not navigable. */
export interface NonNavigableNote {
  id: string;
  title: string;
  color: string;
}

interface TaskLinkedNotesProps {
  taskId: string;
  onViewNote?: (note: { id: string; shortId: string; title: string }) => void;
  /**
   * When provided, skips the authenticated fetch and renders these notes
   * instead (public share mode — notes come from the hydrated share dataset).
   */
  providedNotes?: Note[];
  /** Linked notes outside the share — rendered as dimmed, non-clickable rows. */
  nonNavigableNotes?: NonNavigableNote[];
}

const EMPTY_ICON_SIZE = 40;

/**
 * TaskLinkedNotes renders notes linked to a specific task.
 *
 * Authenticated mode fetches notes by task ID; public share mode receives
 * them via `providedNotes`. Both display `NoteCard` instances; clicking a
 * card opens the note detail. Out-of-scope relations (`nonNavigableNotes`)
 * keep their titles visible but cannot be opened.
 */
export const TaskLinkedNotes = memo(function TaskLinkedNotes({
  taskId,
  onViewNote,
  providedNotes,
  nonNavigableNotes,
}: TaskLinkedNotesProps) {
  const { t } = useTranslation();
  const fetchNotesByTaskId = useGeoMarkupStore(
    (state) => state.notes.fetchNotesByTaskId
  );
  const { openArtifact } = useArtifactNavigation();

  const [fetchedNotes, setFetchedNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(providedNotes === undefined);

  useEffect(() => {
    if (providedNotes !== undefined) return undefined;

    let cancelled = false;

    const loadNotes = async () => {
      setIsLoading(true);
      const notes = await fetchNotesByTaskId(taskId);
      if (!cancelled) {
        setFetchedNotes(notes);
        setIsLoading(false);
      }
    };

    loadNotes();

    return () => {
      cancelled = true;
    };
  }, [taskId, fetchNotesByTaskId, providedNotes]);

  const linkedNotes = providedNotes ?? fetchedNotes;

  const handleNoteClick = useCallback(
    (noteShortId: string) => {
      const note = linkedNotes.find((item) => item.shortId === noteShortId);
      if (!note) return;

      if (onViewNote) {
        onViewNote({ id: note.id, shortId: note.shortId, title: note.title });
      } else {
        openArtifact('notes', 'detail', note.shortId);
      }
    },
    [linkedNotes, onViewNote, openArtifact]
  );

  if (isLoading) {
    return (
      <Stack gap="sm" className={styles.container}>
        <ListItemSkeleton showAvatar={false} />
        <ListItemSkeleton showAvatar={false} />
      </Stack>
    );
  }

  const staticNotes = nonNavigableNotes ?? [];

  if (linkedNotes.length === 0 && staticNotes.length === 0) {
    return (
      <div className={styles.emptyState}>
        <IconNote size={EMPTY_ICON_SIZE} className={styles.emptyIcon} />
        <Text size="sm" fw={500}>
          {t('taskDetail.notes.empty')}
        </Text>
        <Text size="xs" c="dimmed">
          {t('taskDetail.notes.emptyDescription')}
        </Text>
      </div>
    );
  }

  return (
    <Stack gap="sm" className={styles.container}>
      {linkedNotes.map((note) => (
        <NoteCard key={note.id} note={note} onClick={handleNoteClick} />
      ))}
      {staticNotes.map((note) => (
        <div
          key={note.id}
          className={styles.staticNote}
          title={t('share.public.notIncludedInShare')}
        >
          <span
            className={styles.staticNoteDot}
            style={{ '--note-color': note.color } as CSSProperties}
          />
          <Text size="sm" c="dimmed" lineClamp={1}>
            {note.title}
          </Text>
        </div>
      ))}
    </Stack>
  );
});
